import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Car, Settings, Check, 
  Star, Users, Phone, MessageSquare, 
  X, ChevronDown, ChevronUp, User, 
  MapPin, Navigation, Timer, Clock, ArrowRight,
  Building2, Calculator, Heart, LogOut, Calendar, RefreshCw
} from 'lucide-react';
import NearbyTaxiList from '../components/NearbyTaxiList';
import { useGeolocation } from '../hooks/useGeolocation';
import { useNearbyTaxis } from '../hooks/useNearbyTaxis';
import Assistant from '../components/Assistant';
import FavoriteTaxiStands from '../components/FavoriteTaxiStands';
import { useSearchCounter } from '../hooks/useSearchCounter';
import { supabase } from '../lib/supabase';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet CSS'in varsayılan ikonlarını düzeltmek için
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Custom user location icon (blue circle with pulse)
const userIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `
    <div class="relative">
      <div style="
        background-color: #3b82f6;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid #ffffff;
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
      "></div>
      <div style="
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border-radius: 50%;
        background: #3b82f6;
        opacity: 0.3;
        animation: pulse 2s infinite;
      "></div>
      <style>
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            opacity: 0.5;
          }
          70% {
            transform: scale(1.5);
            opacity: 0.3;
          }
          100% {
            transform: scale(0.95);
            opacity: 0.5;
          }
        }
      </style>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// Taksi durağı ikonu
const taxiStandIcon = (name: string) => new L.DivIcon({
  className: 'custom-div-icon',
  html: `
    <div style="
      position: relative;
      width: 30px;
      height: 30px;
    ">
      <div style="
        background-color: #eab308;
        width: 30px;
        height: 30px;
        border-radius: 4px;
        border: 2px solid #ffffff;
        box-shadow: 0 0 10px rgba(234, 179, 8, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: #000;
        font-size: 14px;
      ">T</div>
      <div style="
        position: absolute;
        top: 32px;
        left: 50%;
        transform: translateX(-50%);
        background-color: rgba(0, 0, 0, 0.6);
        color: #ffffff;
        padding: 1px 4px;
        border-radius: 3px;
        font-size: 8px;
        white-space: nowrap;
        text-align: center;
        font-weight: bold;
        max-width: 80px;
        overflow: hidden;
        text-overflow: ellipsis;
      ">${name.length > 12 ? name.substring(0, 12) + '...' : name}</div>
    </div>
  `,
  iconSize: [30, 45],
  iconAnchor: [15, 30],
  popupAnchor: [0, -32],
});

// Default icon düzeltmesi
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Map Updater Component
function MapUpdater({ center, zoom }: { center?: [number, number], zoom?: number }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 14);
    }
  }, [center, zoom, map]);
  
  return null;
}

interface Location {
  latitude: number;
  longitude: number;
  error?: string;
}

interface Place {
  latitude: string | number;
  longitude: string | number;
  name: string;
  address: string;
  phone?: string;
  distance?: string;
  duration?: string;
}

interface UserProfile {
  id: string;
  full_name: string;
  phone: string;
  avatar_url: string | null;
  share_location: boolean;
}

export default function Home() {
  const navigate = useNavigate();
  const { location, loading: locationLoading, getLocation } = useGeolocation();
  const { 
    places, 
    loading: placesLoading, 
    error: placesError, 
    searchNearbyTaxis,
    hasSearchedBefore,
    getRemainingCooldown
  } = useNearbyTaxis();
  const [showNearbyTaxis, setShowNearbyTaxis] = useState(false);
  const [isNearbyListExpanded, setIsNearbyListExpanded] = useState(false);
  const { incrementSearchCount } = useSearchCounter();
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [recentTrips, setRecentTrips] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalTrips: 0,
    totalDistance: 0,
    averageRating: 0
  });
  const [showTravelCompanionModal, setShowTravelCompanionModal] = useState(false);
  const [shareLocationDuration, setShareLocationDuration] = useState(15); // Dakika cinsinden
  const [nearbyUsers, setNearbyUsers] = useState<any[]>([]); // Yakındaki kullanıcılar
  const [isLocationSharing, setIsLocationSharing] = useState(false);
  const [locationSharingEndTime, setLocationSharingEndTime] = useState<Date | null>(null);
  const [remainingShareTime, setRemainingShareTime] = useState(0);
  const [allowCalls, setAllowCalls] = useState(true);
  const [allowMessages, setAllowMessages] = useState(true);
  const [hideNameInitials, setHideNameInitials] = useState(false);
  const [startLocation, setStartLocation] = useState('');
  const [endLocation, setEndLocation] = useState('');
  
  // Harita için yeni state'ler
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number] | undefined>(undefined);
  const [taxiStands, setTaxiStands] = useState<Array<{
    name: string;
    address: string;
    position: [number, number];
    phone?: string;
    distance?: string;
  }>>([]);

  useEffect(() => {
    if (hasSearchedBefore()) {
      setShowNearbyTaxis(true);
    }

    const remaining = getRemainingCooldown();
    setCooldownRemaining(remaining);

    const interval = setInterval(() => {
      const newRemaining = getRemainingCooldown();
      setCooldownRemaining(newRemaining);
    }, 1000);

    // Test kullanıcısı oluştur
    const testProfile = {
      id: 'guest',
      full_name: '',
      phone: '',
      avatar_url: null,
      share_location: false
    };
    
    // Kullanıcı state'ini güncelle
    setUserProfile(testProfile);
    
    // Test yolculukları oluştur
    const testTrips: any[] = [];
    
    // Test yolculuklarını state'e ata
    setRecentTrips(testTrips);
    
    // Test istatistiklerini hesapla
    const testStats = {
      totalTrips: 0,
      totalDistance: 0,
      averageRating: 0
    };
    
    // Test istatistiklerini state'e ata
    setStats(testStats);

    // Auth durumu değişikliklerini dinle
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth durumu değişti:', event, session ? 'Oturum var' : 'Oturum yok');
      if (session?.user) {
        console.log('Oturum açık, profil yükleniyor');
        loadUserProfile();
      } else {
        console.log('Oturum kapalı, profil temizleniyor');
        // Test kullanıcısını korumak için bu satırı yorum satırına alıyoruz
        // setUserProfile(null);
      }
    });

    return () => {
      clearInterval(interval);
      subscription.unsubscribe();
    };
  }, []);

  const loadUserProfile = async () => {
    try {
      console.log('loadUserProfile çağrıldı');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      console.log('Kullanıcı durumu:', user ? 'Giriş yapılmış' : 'Giriş yapılmamış');
      
      if (userError) {
        console.error('Kullanıcı bilgisi alınırken hata:', userError);
        return;
      }
      
      if (!user) {
        console.log('Kullanıcı bulunamadı, profil null olarak ayarlanıyor');
        setUserProfile(null);
        return;
      }

      console.log('Kullanıcı ID:', user.id);
      
      // Profil bilgilerini yükle
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Profil bilgisi alınırken hata:', error);
        return;
      }
      
      console.log('Profil bilgisi:', profile);
      
      if (profile) {
        setUserProfile(profile);
        
        // İsim gizleme ayarını da güncelle
        if (profile.hide_name_initials !== undefined) {
          setHideNameInitials(profile.hide_name_initials);
        }
        
        // Arama ve mesaj izinlerini güncelle
        if (profile.allow_calls !== undefined) {
          setAllowCalls(profile.allow_calls);
        }
        
        if (profile.allow_messages !== undefined) {
          setAllowMessages(profile.allow_messages);
        }
        
        // Konum paylaşım durumunu kontrol et
        if (profile.share_location && profile.location_sharing_until) {
          const endTime = new Date(profile.location_sharing_until);
          const now = new Date();
          
          // Eğer paylaşım süresi hala devam ediyorsa
          if (endTime > now) {
            console.log('Aktif konum paylaşımı bulundu, devam ediliyor...');
            setLocationSharingEndTime(endTime);
            setIsLocationSharing(true);
            
            // Yakındaki kullanıcıları getir
            if (profile.latitude && profile.longitude) {
              const userLocation = {
                latitude: profile.latitude,
                longitude: profile.longitude
              };
              fetchNearbyUsers(userLocation);
            }
          } else {
            // Süre dolmuşsa paylaşımı durdur
            console.log('Konum paylaşım süresi dolmuş, otomatik durduruluyor');
            const { error: updateError } = await supabase
              .from('user_profiles')
              .update({
                share_location: false,
                location_sharing_until: null
              })
              .eq('id', user.id);
              
            if (updateError) {
              console.error('Konum paylaşımı durdurulamadı:', updateError);
            }
          }
        }
        
        // Kullanıcının güzergah bilgilerini yükle
        const { data: userRoute, error: routeError } = await supabase
          .from('user_routes')
          .select('start_location, end_location')
          .eq('user_id', user.id)
          .eq('active', true)
          .single();
          
        if (!routeError && userRoute) {
          if (userRoute.start_location) {
            setStartLocation(userRoute.start_location);
          }
          
          if (userRoute.end_location) {
            setEndLocation(userRoute.end_location);
          }
        }
        
        // Son yolculukları yükle
        const { data: trips, error: tripsError } = await supabase
          .from('trips')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(3);
        
        if (tripsError) {
          console.error('Yolculuk bilgisi alınırken hata:', tripsError);
          return;
        }
        
        console.log('Yolculuk bilgisi:', trips);
        
        if (trips) {
          setRecentTrips(trips);
          
          // İstatistikleri hesapla
          const calculatedStats = trips.reduce((acc, trip) => {
            if (trip.status === 'completed') {
              acc.totalTrips++;
              acc.totalDistance += trip.distance || 0;
              if (trip.rating) {
                acc.averageRating = (acc.averageRating * (acc.totalTrips - 1) + trip.rating) / acc.totalTrips;
              }
            }
            return acc;
          }, {
            totalTrips: 0,
            totalDistance: 0,
            averageRating: 0
          });
          
          console.log('Hesaplanan istatistikler:', calculatedStats);
          setStats(calculatedStats);
        }
      }
    } catch (err) {
      console.error('Kullanıcı profili yüklenirken hata:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setUserProfile(null);
      setIsUserMenuOpen(false);
    } catch (err) {
      console.error('Çıkış yapılırken hata:', err);
    }
  };

  const handleFindNearbyTaxis = async () => {
    try {
      setIsSearching(true);
      incrementSearchCount();
      
      // Konum alma deneme sayısı artırıldı
      let attemptCount = 0;
      const maxAttempts = 3;
      
      const tryGetLocation = async (): Promise<Location | null> => {
        try {
          const newLocation = await getLocation() as Location;
          if (newLocation && !newLocation.error) {
            return newLocation;
          }
          return null;
        } catch (err) {
          console.error(`Konum alınamadı (deneme ${attemptCount+1}/${maxAttempts}):`, err);
          return null;
        }
      };
      
      let userLocation = null;
      
      // Birkaç kez deneme
      while (attemptCount < maxAttempts && !userLocation) {
        userLocation = await tryGetLocation();
        attemptCount++;
        
        // Eğer konum alınamadıysa ve hala deneme hakkımız varsa kısa bekle
        if (!userLocation && attemptCount < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (userLocation) {
        const result = await searchNearbyTaxis({ 
          lat: userLocation.latitude, 
          lng: userLocation.longitude 
        });
        
        // Haritayı göster ve merkezi kullanıcının konumuna ayarla
        setMapCenter([userLocation.latitude, userLocation.longitude]);
        setShowMap(true);
        
        // Taksi durakları için yapay koordinatlar oluşturacağız
        // gerçek veriler yoksa veya enlem/boylam bilgisi eksikse
        if (places && places.length > 0) {
          console.log("Taksi durakları:", places);
          
          // Kullanıcının konumu etrafında taksi durakları için yapay koordinatlar oluştur
          const formattedStands = places.map((place, index) => {
            // Gerçek koordinatlar varsa onları kullan
            if (place.latitude && place.longitude) {
              // Konum bilgisi string formatında olabilir, sayıya çeviriyoruz
              let lat = typeof place.latitude === 'string' ? parseFloat(place.latitude) : place.latitude;
              let lng = typeof place.longitude === 'string' ? parseFloat(place.longitude) : place.longitude;
              
              // Eğer geçerli bir enlem ve boylam değilse veya sıfır ise
              if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
                // Rastgele konum oluştur
                const distanceKm = parseFloat(place.distance?.replace(' km', '') || '0.5');
                const angle = Math.random() * Math.PI * 2;
                const latOffset = Math.cos(angle) * distanceKm * 0.009;
                const lngOffset = Math.sin(angle) * distanceKm * 0.009;
                
                return {
                  name: place.name,
                  address: place.address,
                  position: [
                    userLocation.latitude + latOffset,
                    userLocation.longitude + lngOffset
                  ] as [number, number],
                  phone: place.phone,
                  distance: place.distance
                };
              }
              
              return {
                name: place.name,
                address: place.address,
                position: [lat, lng] as [number, number],
                phone: place.phone,
                distance: place.distance
              };
            } 
            
            // Gerçek koordinat yoksa, kullanıcının konumu etrafında rastgele noktalar üret
            // Mesafe bilgisinden yaklaşık bir lokasyon hesapla
            const distanceKm = parseFloat(place.distance?.replace(' km', '') || '0.5');
            
            // Rastgele bir açı oluştur (0-360 derece)
            const angle = Math.random() * Math.PI * 2;
            
            // Yaklaşık olarak 1 km = 0.009 derece enlem/boylam
            // distanceKm değerini 0.009 ile çarparak yaklaşık bir mesafe elde ediyoruz
            const latOffset = Math.cos(angle) * distanceKm * 0.009;
            const lngOffset = Math.sin(angle) * distanceKm * 0.009;
            
            return {
              name: place.name,
              address: place.address,
              position: [
                userLocation.latitude + latOffset,
                userLocation.longitude + lngOffset
              ] as [number, number],
              phone: place.phone,
              distance: place.distance
            };
          });
          
          setTaxiStands(formattedStands);
          console.log("Haritadaki taksi durakları:", formattedStands);
        }
        
        setShowNearbyTaxis(true);
        setIsNearbyListExpanded(true);
      } else {
        console.error('Konum alınamadı');
      }
    } catch (error) {
      console.error('Yakındaki taksiler aranırken hata oluştu:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const formatCooldown = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const userLocation = location ? { lat: location.latitude, lng: location.longitude } : null;

  // Konum paylaşımını başlat
  const startLocationSharing = async () => {
    try {
      // Kullanıcı giriş yapmış mı kontrol et
      if (!userProfile || userProfile.id === 'guest') {
        // Sessizce giriş sayfasına yönlendir
        setShowTravelCompanionModal(false);
        navigate('/giris');
        return;
      }

      // Tüm konum alma işlemleri için bir wrapper fonksiyon
      const getCurrentLocation = async (): Promise<Location | null> => {
        // Önce mevcut konumu dene
        if (location && location.latitude && location.longitude) {
          return location;
        }
        
        try {
          // Mevcut konum yoksa yeni konum al
          const newLocation = await getLocation() as Location;
          if (newLocation && !newLocation.error && newLocation.latitude && newLocation.longitude) {
            return newLocation;
          }
          return null;
        } catch (err) {
          console.error('Konum alınırken hata:', err);
          return null;
        }
      };
      
      // Konum alma işlemini başlat
      const currentLocation = await getCurrentLocation();
      
      // Konum alınamazsa sessizce çık
      if (!currentLocation) {
        console.error('Konum alınamadı, işlem iptal ediliyor');
        // Modal'ı kapat ama hata mesajı gösterme
        setShowTravelCompanionModal(false);
        return;
      }

      // Konum paylaşımı bitiş zamanını ayarla
      const endTime = new Date();
      endTime.setMinutes(endTime.getMinutes() + shareLocationDuration);
      setLocationSharingEndTime(endTime);
      setIsLocationSharing(true);
      
      // Kullanıcının konum bilgilerini veritabanına kaydet
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          last_location_update: new Date().toISOString(),
          share_location: true,
          location_sharing_until: endTime.toISOString(),
          allow_calls: allowCalls,
          allow_messages: allowMessages,
          hide_name_initials: hideNameInitials
        })
        .eq('id', userProfile.id);
      
      if (updateError) {
        console.error('Konum paylaşımı başlatılırken hata:', updateError);
        // Hata gösterme, sessizce iptal et
        setIsLocationSharing(false);
        setShowTravelCompanionModal(false);
        return;
      }
      
      // Güzergah bilgilerini ayrı bir tabloya kaydet (eğer varsa)
      if (startLocation || endLocation) {
        const { error: routeError } = await supabase
          .from('user_routes')
          .upsert({
            user_id: userProfile.id,
            start_location: startLocation,
            end_location: endLocation,
            created_at: new Date().toISOString(),
            active: true
          }, {
            onConflict: 'user_id'
          });
          
        if (routeError) {
          console.error('Güzergah bilgisi kaydedilirken hata:', routeError);
          // Hatayı gösterme, sadece loglama yap
        }
      }
      
      console.log(`Konum ${shareLocationDuration} dakika boyunca paylaşılacak, bitiş: ${endTime.toLocaleTimeString()}`);
      
      // Yakındaki kullanıcıları getir
      fetchNearbyUsers(currentLocation);
      
      // İlk sorgulama başarılı ise, modal'ı kapat
      setShowTravelCompanionModal(false);
      
      // Bildirim göster
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Taksi Mobil', {
          body: `Konumunuz ${shareLocationDuration} dakika boyunca paylaşılıyor.`,
          icon: '/icons/logo-192.png'
        });
      }
    } catch (err) {
      console.error('Konum paylaşılırken hata:', err);
      // Hata gösterme, sessizce iptal et
      setIsLocationSharing(false);
      setShowTravelCompanionModal(false);
    }
  };

  // Yakındaki kullanıcıları getir
  const fetchNearbyUsers = async (userLocation?: Location | null) => {
    try {
      // Kullanıcı bilgisi kontrolü
      if (!userProfile || userProfile.id === 'guest') {
        console.error('fetchNearbyUsers: Kullanıcı giriş yapmamış');
        return;
      }
      
      // Konum kontrolü - parametreden veya state'ten al
      const currentLocation = userLocation || location;
      if (!currentLocation || !currentLocation.latitude || !currentLocation.longitude) {
        console.error('fetchNearbyUsers: Konum bilgisi yok');
        return;
      }

      // Kullanıcının konumuna yakın ve konum paylaşımı açık olan kullanıcıları getir
      const now = new Date().toISOString();
      const maxDistance = 0.045; // Yaklaşık 5 km yarıçap
      
      console.log('Yakındaki kullanıcılar getiriliyor:', {
        userID: userProfile.id,
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        time: now
      });
      
      // Kullanıcıların konum bilgilerini al
      const { data: userData, error: userError } = await supabase
        .from('user_profiles')
        .select('id, full_name, phone, latitude, longitude, avatar_url, allow_calls, allow_messages, hide_name_initials, location_sharing_until')
        .neq('id', userProfile.id) // Kendisi hariç
        .eq('share_location', true) // Konum paylaşımı açık olanlar
        .gte('location_sharing_until', now) // Konum paylaşım süresi dolmamış olanlar
        .gt('latitude', currentLocation.latitude - maxDistance)
        .lt('latitude', currentLocation.latitude + maxDistance)
        .gt('longitude', currentLocation.longitude - maxDistance)
        .lt('longitude', currentLocation.longitude + maxDistance);
      
      if (userError) {
        console.error('Yakındaki kullanıcılar getirilirken hata:', userError);
        return;
      }
      
      if (!userData || userData.length === 0) {
        console.log('Yakında konum paylaşan kullanıcı bulunamadı');
        setNearbyUsers([]);
        return;
      }

      // Her kullanıcı için güzergah bilgilerini al
      const userIds = userData.map(user => user.id);
      const { data: routeData, error: routeError } = await supabase
        .from('user_routes')
        .select('user_id, start_location, end_location')
        .in('user_id', userIds)
        .eq('active', true);
        
      if (routeError) {
        console.error('Yakındaki kullanıcıların güzergah bilgileri alınırken hata:', routeError);
      }
      
      // Güzergah bilgilerini kullanıcı verisiyle birleştir
      const mergedData = userData.map(user => {
        const userRoute = routeData?.find(route => route.user_id === user.id);
        return {
          ...user,
          start_location: userRoute?.start_location || null,
          end_location: userRoute?.end_location || null
        };
      });

      // Kullanıcıları mesafeye göre hesapla ve sırala
      const usersWithDistance = mergedData.map(user => {
        const distance = calculateDistance(
          currentLocation.latitude, 
          currentLocation.longitude, 
          user.latitude || 0, 
          user.longitude || 0
        );
        
        return {
          ...user,
          distance: parseFloat(distance.toFixed(1))
        };
      });
      
      // Mesafeye göre sırala (en yakından uzağa)
      const sortedUsers = usersWithDistance.sort((a, b) => a.distance - b.distance);
      
      setNearbyUsers(sortedUsers);
      console.log('Yakındaki kullanıcılar bulundu:', sortedUsers.length);
    } catch (err) {
      console.error('Yakındaki kullanıcılar getirilirken hata:', err);
    }
  };

  // İki nokta arasındaki mesafeyi hesapla (Haversine formülü)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Dünya yarıçapı (km)
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Kilometre cinsinden mesafe
  };

  // Radyan dönüşümü
  const toRad = (value: number) => {
    return value * Math.PI / 180;
  };

  // Konum paylaşımını durdur
  const stopLocationSharing = async () => {
    try {
      if (userProfile && userProfile.id !== 'guest') {
        // Konum paylaşımını veritabanında güncelle
        const { error } = await supabase
          .from('user_profiles')
          .update({
            share_location: false,
            location_sharing_until: null
          })
          .eq('id', userProfile.id);
          
        if (error) {
          console.error('Konum paylaşımı durdurulurken hata:', error);
          alert('Konum paylaşımı durdurulamadı: ' + error.message);
        }
        
        // Güzergah bilgilerini de aktif olarak işaretle
        const { error: routeError } = await supabase
          .from('user_routes')
          .update({
            active: false
          })
          .eq('user_id', userProfile.id);
          
        if (routeError) {
          console.error('Güzergah bilgisi güncellenirken hata:', routeError);
        }
      }
      
      setIsLocationSharing(false);
      setLocationSharingEndTime(null);
      setNearbyUsers([]);
      setStartLocation('');
      setEndLocation('');
      console.log('Konum paylaşımı durduruldu');
      
      // Bildirim göster
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Taksi Mobil', {
          body: 'Konum paylaşımınız durduruldu.',
          icon: '/icons/logo-192.png'
        });
      }
    } catch (err) {
      console.error('Konum paylaşımı durdurulurken hata:', err);
      alert('Konum paylaşımı durdurulurken bir hata oluştu.');
    }
  };

  // Kalan süreyi güncelle ve konum bilgisini periyodik olarak güncelle
  useEffect(() => {
    if (!isLocationSharing || !locationSharingEndTime) return;

    const intervalId = setInterval(async () => {
      const now = new Date();
      const diff = Math.max(0, Math.floor((locationSharingEndTime.getTime() - now.getTime()) / 1000));
      
      setRemainingShareTime(diff);
      
      if (diff <= 0) {
        stopLocationSharing();
        clearInterval(intervalId);
        return;
      }
      
      // Her 30 saniyede bir konum bilgisini güncelle ve yakındaki kullanıcıları yeniden getir
      if (diff % 30 === 0 && userProfile && location) {
        try {
          // Konum bilgisini güncelle
          const { error } = await supabase
            .from('user_profiles')
            .update({
              latitude: location.latitude,
              longitude: location.longitude,
              last_location_update: new Date().toISOString()
            })
            .eq('id', userProfile.id);
            
          if (error) {
            console.error('Konum güncellenirken hata:', error);
          } else {
            // Yakındaki kullanıcıları yeniden getir
            await fetchNearbyUsers();
          }
        } catch (err) {
          console.error('Konum güncellenirken hata:', err);
        }
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isLocationSharing, locationSharingEndTime, userProfile, location]);

  // İsim görüntüleme fonksiyonu - Gizleme seçeneğine göre ismi formatlar
  const formatName = (fullName: string) => {
    // İsim ve soyismi ayır
    const nameParts = fullName.split(' ');
    
    // Her bir parçanın ilk harfini göster, geri kalan kısmını * ile değiştir
    const maskedName = nameParts.map(part => {
      if (part.length <= 1) return part;
      return part.charAt(0) + '*'.repeat(part.length - 1);
    }).join(' ');
    
    return maskedName;
  };

  // Boolean değerleri güvenli bir şekilde parse eden yardımcı fonksiyon
  const parseBoolean = (value: any): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    if (typeof value === 'number') return value === 1;
    return false;
  };

  // Kalan süreyi formatla
  const formatRemainingTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Kullanıcıyı ara - izin kontrolü ile
  const callUser = (phone: string) => {
    if (allowCalls) {
      window.location.href = `tel:${phone}`;
    } else {
      alert('Arama izni vermediğiniz için bu kullanıcıyı arayamazsınız.');
    }
  };

  // Kullanıcıya mesaj gönder - izin kontrolü ile
  const messageUser = (phone: string) => {
    if (allowMessages) {
      window.location.href = `sms:${phone}`;
    } else {
      alert('Mesaj izni vermediğiniz için bu kullanıcıya mesaj gönderemezsiniz.');
    }
  };

  // Bildirim izni için kontrol ekleniyor
  useEffect(() => {
    // Bildirim izni kontrol et
    if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Kullanıcı Paneli */}
      {userProfile && userProfile.id === 'guest' ? (
        <section className="px-4 sm:px-0 animate-fade-in">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-yellow-400/90 to-yellow-500/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8">
              <div className="relative space-y-8">
                {/* Üst Kısım - En Yakın Taksi Bölümü */}
                <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-5 border border-black/10">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="p-3 bg-black/10 rounded-xl shadow-inner">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-black">En Yakın Taksi</h2>
                      <p className="text-xs text-black/70">Bir tıkla en yakındaki taksiyi çağırın!</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <button
                      onClick={handleFindNearbyTaxis}
                      disabled={locationLoading || placesLoading || cooldownRemaining > 0}
                      className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed group hover:scale-[1.02]"
                    >
                      {locationLoading || placesLoading ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>Konum Alınıyor...</span>
                        </>
                      ) : cooldownRemaining > 0 ? (
                        <>
                          <Clock className="w-4 h-4" />
                          <span>Lütfen bekleyin: {formatCooldown(cooldownRemaining)}</span>
                        </>
                      ) : (
                        <>                
                          <span>Taksi Bul</span>
                          <MapPin className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => setShowTravelCompanionModal(true)}
                      disabled={locationLoading}
                      className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-white/10 hover:bg-white/20 text-black text-sm font-medium rounded-xl transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed group hover:scale-[1.02]"
                    >
                      {locationLoading ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>Konum Alınıyor...</span>
                        </>
                      ) : isLocationSharing ? (
                        <>
                          <Timer className="w-4 h-4" />
                          <span>Konum Paylaşılıyor: {formatRemainingTime(remainingShareTime)}</span>
                        </>
                      ) : (
                        <>
                          <span>Paylaşımlı Yolculuk</span>
                          <Users className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                  </div>

                  {/* Nearby Taxis List */}
                  {showNearbyTaxis && (
                    <div className="mt-4 border-t border-black/10 pt-4">
                      {/* Harita Gösterimi */}
                      {showMap && mapCenter && (
                        <div className="mb-4 rounded-xl overflow-hidden border border-yellow-500/30 shadow-lg">
                          <div className="w-full h-[250px] relative bg-[#111111]">
                            <div className="absolute inset-0 z-0 flex items-center justify-center">
                              <div className="animate-pulse text-yellow-500 text-sm">Harita yükleniyor...</div>
                            </div>
                            <MapContainer 
                              center={mapCenter} 
                              zoom={13} 
                              style={{ height: '100%', width: '100%' }}
                              zoomControl={true}
                              attributionControl={false}
                              className="z-10"
                            >
                              <TileLayer
                                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                              />
                              
                              {/* Kullanıcı Konumu */}
                              <Marker position={mapCenter} icon={userIcon}>
                                <Popup>
                                  <div className="text-xs font-medium">Bulunduğunuz Konum</div>
                                </Popup>
                              </Marker>
                              
                              {/* Taksi Durakları */}
                              {taxiStands.map((stand, index) => (
                                <Marker 
                                  key={index} 
                                  position={stand.position} 
                                  icon={taxiStandIcon(stand.name)}
                                >
                                  <Popup 
                                    maxWidth={180} 
                                    minWidth={120}
                                    className="custom-popup" 
                                    autoPanPadding={[20, 20]}
                                  >
                                    <div className="text-sm">
                                      <div className="font-bold text-yellow-500 text-sm mb-1 border-b border-yellow-500/20 pb-1">
                                        {stand.name}
                                      </div>
                                      {stand.distance && (
                                        <div className="flex items-center text-xs text-white/70 mb-1">
                                          <Navigation className="w-3 h-3 mr-1 flex-shrink-0 text-yellow-500" />
                                          <span>{stand.distance}</span>
                                        </div>
                                      )}
                                      {stand.phone && (
                                        <a
                                          href={`tel:${stand.phone}`}
                                          className="block w-full mt-2 bg-yellow-500 hover:bg-yellow-600 text-black py-1.5 rounded text-center text-xs font-medium transition-colors"
                                        >
                                          Taksi Çağır
                                        </a>
                                      )}
                                    </div>
                                  </Popup>
                                </Marker>
                              ))}
                              
                              {/* Harita Updater */}
                              <MapUpdater center={mapCenter} zoom={13} />
                            </MapContainer>
                          </div>
                        </div>
                      )}
                      
                      <div className="rounded-xl px-0.5 pb-0.5">
                        <NearbyTaxiList
                          places={places}
                          loading={placesLoading}
                          error={placesError || undefined}
                          isExpanded={isNearbyListExpanded}
                          onToggle={() => setIsNearbyListExpanded(!isNearbyListExpanded)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Üst Kısım - İstatistikler */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-4 group hover:bg-black/10 transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <Car className="w-5 h-5 text-black/70" />
                      <span className="text-xs font-medium text-black/50">81 İl</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-black group-hover:translate-x-1 transition-transform">14.2K+</h3>
                      <p className="text-xs text-black/60 mt-0.5">Aktif Taksi</p>
                    </div>
                  </div>
                  
                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-4 group hover:bg-black/10 transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <Users className="w-5 h-5 text-black/70" />
                      <span className="text-xs font-medium text-black/50">Günlük</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-black group-hover:translate-x-1 transition-transform">2.8K+</h3>
                      <p className="text-xs text-black/60 mt-0.5">Paylaşımlı</p>
                    </div>
                  </div>
                  
                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-4 group hover:bg-black/10 transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <Building2 className="w-5 h-5 text-black/70" />
                      <span className="text-xs font-medium text-black/50">Türkiye</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-black group-hover:translate-x-1 transition-transform">95.2K+</h3>
                      <p className="text-xs text-black/60 mt-0.5">Durak</p>
                    </div>
                  </div>
                  
                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-4 group hover:bg-black/10 transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <Clock className="w-5 h-5 text-black/70" />
                      <span className="text-xs font-medium text-black/50">Ortalama</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-black group-hover:translate-x-1 transition-transform">3-5</h3>
                      <p className="text-xs text-black/60 mt-0.5">Dk Bekleme</p>
                    </div>
                  </div>
                </div>

                {/* Hızlı Erişim Butonları */}
                <div className="space-y-3">
                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-black/10 transition-all border border-black/5">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-yellow-500 rounded-xl flex-shrink-0">
                        <Building2 className="w-6 h-6 text-black" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-black font-medium text-base">Şehir Seçimi</h3>
                        <p className="text-xs text-black/70">İstediğiniz şehirdeki taksi duraklarını görüntüleyin</p>
                      </div>
                      <button
                        onClick={() => navigate('/sehirler')}
                        className="p-2.5 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white rounded-xl transition-all"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-black/10 transition-all border border-black/5">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-yellow-500 rounded-xl flex-shrink-0">
                        <Calculator className="w-6 h-6 text-black" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-black font-medium text-base">Ücret Hesaplama</h3>
                        <p className="text-xs text-black/70">Yolculuk ücretinizi önceden hesaplayın</p>
                      </div>
                      <button
                        onClick={() => navigate('/taksi-ucreti-hesaplama')}
                        className="p-2.5 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white rounded-xl transition-all"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Alt Kısım - Butonlar */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={() => navigate('/kayit-ol')}
                    className="w-full sm:w-auto px-6 py-3 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white rounded-xl transition-all text-sm font-medium flex items-center justify-center gap-2 group"
                  >
                    <span>Hemen Kayıt Ol</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => navigate('/giris')}
                    className="w-full sm:w-auto px-6 py-3 bg-white/10 hover:bg-white/20 text-black rounded-xl transition-all text-sm font-medium"
                  >
                    Giriş Yap
                  </button>
                </div>

                {/* En Alt Kısım - Bilgi Metni */}
                <div className="text-center">
                  <p className="text-xs text-black/60">
                    Türkiye'nin en büyük taksi ağına katılın, güvenli ve konforlu yolculuğun keyfini çıkarın.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : userProfile && (
        <section className="px-4 sm:px-0 animate-fade-in">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-yellow-400/90 to-yellow-500/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8">
              <div className="relative space-y-8">
                {/* Üst Kısım - Kullanıcı Bilgileri */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {userProfile.avatar_url ? (
                      <img 
                        src={userProfile.avatar_url} 
                        alt={userProfile.full_name}
                        className="w-16 h-16 rounded-xl object-cover border-2 border-yellow-300"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-yellow-300 rounded-xl flex items-center justify-center">
                        <User className="w-8 h-8 text-gray-900" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-bold text-black">
                        Hoş Geldin, {userProfile.full_name}
                      </h2>
                      <p className="text-sm text-black/70">{userProfile.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => navigate('/profil')}
                      className="p-3 bg-black/10 hover:bg-black/20 rounded-xl transition-all"
                    >
                      <User className="w-5 h-5 text-black" />
                    </button>
                    <button 
                      onClick={() => navigate('/favoriler')}
                      className="p-3 bg-black/10 hover:bg-black/20 rounded-xl transition-all"
                    >
                      <Heart className="w-5 h-5 text-black" />
                    </button>
                    <button 
                      onClick={handleLogout}
                      className="p-3 bg-black/10 hover:bg-black/20 rounded-xl transition-all"
                    >
                      <LogOut className="w-5 h-5 text-black" />
                    </button>
                  </div>
                </div>

                {/* Üst Kısım - En Yakın Taksi Bölümü */}
                <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-5 border border-black/10">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="p-3 bg-black/10 rounded-xl shadow-inner">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-black">En Yakın Taksi</h2>
                      <p className="text-xs text-black/70">Bir tıkla en yakındaki taksiyi çağırın!</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <button
                      onClick={handleFindNearbyTaxis}
                      disabled={locationLoading || placesLoading || cooldownRemaining > 0}
                      className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed group hover:scale-[1.02]"
                    >
                      {locationLoading || placesLoading ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>Konum Alınıyor...</span>
                        </>
                      ) : cooldownRemaining > 0 ? (
                        <>
                          <Clock className="w-4 h-4" />
                          <span>Lütfen bekleyin: {formatCooldown(cooldownRemaining)}</span>
                        </>
                      ) : (
                        <>                
                          <span>Taksi Bul</span>
                          <MapPin className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => setShowTravelCompanionModal(true)}
                      disabled={locationLoading}
                      className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-white/10 hover:bg-white/20 text-black text-sm font-medium rounded-xl transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed group hover:scale-[1.02]"
                    >
                      {locationLoading ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>Konum Alınıyor...</span>
                        </>
                      ) : isLocationSharing ? (
                        <>
                          <Timer className="w-4 h-4" />
                          <span>Konum Paylaşılıyor: {formatRemainingTime(remainingShareTime)}</span>
                        </>
                      ) : (
                        <>
                          <span>Paylaşımlı Yolculuk</span>
                          <Users className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                  </div>

                  {/* Nearby Taxis List */}
                  {showNearbyTaxis && (
                    <div className="mt-4 border-t border-black/10 pt-4">
                      {/* Harita Gösterimi */}
                      {showMap && mapCenter && (
                        <div className="mb-4 rounded-xl overflow-hidden border border-yellow-500/30 shadow-lg">
                          <div className="w-full h-[250px] relative bg-[#111111]">
                            <div className="absolute inset-0 z-0 flex items-center justify-center">
                              <div className="animate-pulse text-yellow-500 text-sm">Harita yükleniyor...</div>
                            </div>
                            <MapContainer 
                              center={mapCenter} 
                              zoom={13} 
                              style={{ height: '100%', width: '100%' }}
                              zoomControl={true}
                              attributionControl={false}
                              className="z-10"
                            >
                              <TileLayer
                                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                              />
                              
                              {/* Kullanıcı Konumu */}
                              <Marker position={mapCenter} icon={userIcon}>
                                <Popup>
                                  <div className="text-xs font-medium">Bulunduğunuz Konum</div>
                                </Popup>
                              </Marker>
                              
                              {/* Taksi Durakları */}
                              {taxiStands.map((stand, index) => (
                                <Marker 
                                  key={index} 
                                  position={stand.position} 
                                  icon={taxiStandIcon(stand.name)}
                                >
                                  <Popup 
                                    maxWidth={180} 
                                    minWidth={120}
                                    className="custom-popup" 
                                    autoPanPadding={[20, 20]}
                                  >
                                    <div className="text-sm">
                                      <div className="font-bold text-yellow-500 text-sm mb-1 border-b border-yellow-500/20 pb-1">
                                        {stand.name}
                                      </div>
                                      {stand.distance && (
                                        <div className="flex items-center text-xs text-white/70 mb-1">
                                          <Navigation className="w-3 h-3 mr-1 flex-shrink-0 text-yellow-500" />
                                          <span>{stand.distance}</span>
                                        </div>
                                      )}
                                      {stand.phone && (
                                        <a
                                          href={`tel:${stand.phone}`}
                                          className="block w-full mt-2 bg-yellow-500 hover:bg-yellow-600 text-black py-1.5 rounded text-center text-xs font-medium transition-colors"
                                        >
                                          Taksi Çağır
                                        </a>
                                      )}
                                    </div>
                                  </Popup>
                                </Marker>
                              ))}
                              
                              {/* Harita Updater */}
                              <MapUpdater center={mapCenter} zoom={13} />
                            </MapContainer>
                          </div>
                        </div>
                      )}
                      
                      <div className="rounded-xl px-0.5 pb-0.5">
                        <NearbyTaxiList
                          places={places}
                          loading={placesLoading}
                          error={placesError || undefined}
                          isExpanded={isNearbyListExpanded}
                          onToggle={() => setIsNearbyListExpanded(!isNearbyListExpanded)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Nearby Users Section - Only visible when location is being shared */}
                {isLocationSharing && (
                  <div className="mt-4 border-t border-black/10 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold text-black flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Yakınımdaki Yol Arkadaşları
                      </h2>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={async () => {
                            if (!location) {
                              const newLocation = await getLocation() as Location;
                              if (newLocation && !newLocation.error) {
                                fetchNearbyUsers(newLocation);
                              } else {
                                alert('Konum bilgisi alınamadı. Lütfen konum izni verdiğinizden emin olun.');
                              }
                            } else {
                              fetchNearbyUsers(location);
                            }
                          }}
                          className="p-2 bg-black/10 text-black rounded-lg hover:bg-black/20 transition-colors"
                          title="Listeyi Yenile"
                        >
                          <RefreshCw className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-1 bg-black/10 px-3 py-1 rounded-xl">
                          <Timer className="w-4 h-4 text-black" />
                          <span className="text-sm text-black font-medium">{formatRemainingTime(remainingShareTime)}</span>
                        </div>
                        <button 
                          onClick={stopLocationSharing}
                          className="p-2 bg-black/10 text-black rounded-lg hover:bg-black/20 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {nearbyUsers.length > 0 ? (
                        nearbyUsers.map(user => (
                          <div key={user.id} className="bg-black/5 rounded-xl p-4 border border-black/10 hover:bg-black/10 transition-colors">
                            <div className="flex items-start sm:items-center gap-3">
                              {user.avatar_url ? (
                                <img 
                                  src={user.avatar_url} 
                                  alt={user.full_name}
                                  className="w-10 h-10 rounded-xl object-cover border border-black/10 flex-shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-[#0e0d0d]/20 rounded-xl flex items-center justify-center border border-black/10 flex-shrink-0">
                                  <User className="w-5 h-5 text-black" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col justify-between h-full">
                                  <div>
                                    <h3 className="font-medium text-black flex items-center gap-2 flex-wrap">
                                      <span className="truncate max-w-[150px]">
                                        {parseBoolean(user.hide_name_initials) ? formatName(user.full_name) : user.full_name}
                                      </span>
                                      <span className="text-xs text-black/60 font-normal bg-black/5 px-1.5 py-0.5 rounded-full">
                                        {user.distance} km
                                      </span>
                                    </h3>
                                  </div>
                                  <div className="flex items-center justify-between mt-2">
                                    {user.end_location && (
                                      <div className="inline-flex items-center text-xs text-black/70">
                                        <ArrowRight className="w-3 h-3 mr-1 text-black/80 flex-shrink-0" />
                                        <span className="truncate max-w-[100px]">{user.end_location}</span>
                                      </div>
                                    )}
                                    {user.location_sharing_until && (
                                      <div className="inline-flex items-center text-xs text-black/60 ml-4">
                                        <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                                        <span>
                                          {formatRemainingTime(Math.max(0, Math.floor((new Date(user.location_sharing_until).getTime() - new Date().getTime()) / 1000)))}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button 
                                  onClick={() => user.allow_calls ? callUser(user.phone) : alert('Bu kullanıcı aramaya izin vermiyor.')}
                                  className={`p-2 ${user.allow_calls ? 'bg-[#0e0d0d] text-white hover:bg-[#1e1d1d]' : 'bg-black/10 text-black/40 cursor-not-allowed'} rounded-lg transition-colors`}
                                  disabled={!user.allow_calls}
                                >
                                  <Phone className="w-5 h-5" />
                                </button>
                                <button 
                                  onClick={() => user.allow_messages ? messageUser(user.phone) : alert('Bu kullanıcı mesaj almaya izin vermiyor.')}
                                  className={`p-2 ${user.allow_messages ? 'bg-[#0e0d0d] text-white hover:bg-[#1e1d1d]' : 'bg-black/10 text-black/40 cursor-not-allowed'} rounded-lg transition-colors`}
                                  disabled={!user.allow_messages}
                                >
                                  <MessageSquare className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="bg-black/5 rounded-xl p-4 border border-black/10 text-center">
                          <p className="text-black/70 text-sm">Yakınlarda paylaşımlı yolculuk yapan kimse yok.</p>
                          <p className="text-black/50 text-xs mt-1">Listeyi yenilemek için yukarıdaki yenile butonuna tıklayabilirsiniz.</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Aramaları yönetmek için yeni bölüm */}
                    {nearbyUsers.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-black/10">
                        <p className="text-sm text-black/70 mb-3">Yakınlarda yolculuk paylaşmak isteyen {nearbyUsers.length} kişi bulundu.</p>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <button 
                            onClick={() => {
                              // Tüm uygun kişilere mesaj gönder
                              const eligibleUsers = nearbyUsers.filter(u => u.allow_messages);
                              if (eligibleUsers.length === 0) {
                                alert('Mesaj almaya izin veren kullanıcı bulunmuyor.');
                                return;
                              }
                              const phoneNumbers = eligibleUsers.map(u => u.phone).join(',');
                              window.location.href = `sms:${phoneNumbers}?body=Merhaba, taksi mobil uygulaması üzerinden ortak yolculuk paylaşmak istiyorum.`;
                            }}
                            className="py-2.5 px-4 bg-black/10 hover:bg-black/20 text-black rounded-xl flex items-center justify-center gap-2 transition-colors font-medium"
                          >
                            <MessageSquare className="w-4 h-4" />
                            <span>Toplu Mesaj</span>
                          </button>
                          
                          <button 
                            onClick={stopLocationSharing}
                            className="py-2.5 px-4 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white rounded-xl flex items-center justify-center gap-2 transition-colors font-medium"
                          >
                            <X className="w-4 h-4" />
                            <span>Paylaşımı Durdur</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* İstatistikler */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-black/5 backdrop-blur-sm rounded-xl p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Car className="w-5 h-5 text-black/70" />
                      <p className="text-black/70 font-medium">Toplam Yolculuk</p>
                    </div>
                    <p className="text-2xl font-bold text-black">{stats.totalTrips}</p>
                  </div>
                  <div className="bg-black/5 backdrop-blur-sm rounded-xl p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <MapPin className="w-5 h-5 text-black/70" />
                      <p className="text-black/70 font-medium">Toplam Mesafe</p>
                    </div>
                    <p className="text-2xl font-bold text-black">{stats.totalDistance.toFixed(1)} km</p>
                  </div>
                  <div className="bg-black/5 backdrop-blur-sm rounded-xl p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Star className="w-5 h-5 text-black/70" />
                      <p className="text-black/70 font-medium">Ortalama Puan</p>
                    </div>
                    <p className="text-2xl font-bold text-black flex items-center justify-center">
                      {stats.averageRating.toFixed(1)} <Star className="w-4 h-4 ml-1 text-yellow-500 fill-current" />
                    </p>
                  </div>
                </div>

                {/* Hızlı Erişim Butonları */}
                <div className="space-y-3">
                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-black/10 transition-all border border-black/5">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-yellow-500 rounded-xl flex-shrink-0">
                        <Building2 className="w-6 h-6 text-black" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-black font-medium text-base">Şehir Seçimi</h3>
                        <p className="text-xs text-black/70">İstediğiniz şehirdeki taksi duraklarını görüntüleyin</p>
                      </div>
                      <button
                        onClick={() => navigate('/sehirler')}
                        className="p-2.5 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white rounded-xl transition-all"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-black/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-black/10 transition-all border border-black/5">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-yellow-500 rounded-xl flex-shrink-0">
                        <Calculator className="w-6 h-6 text-black" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-black font-medium text-base">Ücret Hesaplama</h3>
                        <p className="text-xs text-black/70">Yolculuk ücretinizi önceden hesaplayın</p>
                      </div>
                      <button
                        onClick={() => navigate('/taksi-ucreti-hesaplama')}
                        className="p-2.5 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white rounded-xl transition-all"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Son Yolculuklar */}
                {recentTrips.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-black mb-3">Son Yolculuklar</h3>
                    <div className="space-y-3">
                      {recentTrips.map((trip) => (
                        <div key={trip.id} className="bg-black/5 backdrop-blur-sm rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-black/70" />
                              <span className="text-xs text-black/70">
                                {new Date(trip.created_at).toLocaleDateString('tr-TR')}
                              </span>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              trip.status === 'completed' 
                                ? 'bg-green-500/20 text-green-800'
                                : trip.status === 'cancelled'
                                ? 'bg-red-500/20 text-red-800'
                                : 'bg-blue-500/20 text-blue-800'
                            }`}>
                              {trip.status === 'completed' 
                                ? 'Tamamlandı' 
                                : trip.status === 'cancelled'
                                ? 'İptal'
                                : 'Devam Ediyor'}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-black truncate">{trip.pickup_location} → {trip.dropoff_location}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-black/70">{trip.distance} km</span>
                            {trip.rating && (
                              <div className="flex items-center">
                                <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                <span className="text-xs ml-1 text-black/70">{trip.rating}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Assistant */}
      <Assistant 
        userLocation={userLocation}
        places={places}
        isSearching={isSearching}
      />

      {/* Hero Section */}
      <section className="px-4 sm:px-0">
        <div className="max-w-3xl mx-auto">
          <div className="p-6 sm:p-8">
            {/* Yakındaki taksi bölümü ve listeleri sarı kutuya taşındı */}
          </div>
        </div>
      </section>

      {/* Yol Arkadaşı Modal */}
      {showTravelCompanionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-gradient-to-br from-yellow-400/90 to-yellow-500/90 rounded-3xl p-5 max-w-md w-full border border-black/10 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-yellow-400/90 py-1 z-10">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-[#0e0d0d] rounded-xl">
                  <Users className="w-5 h-5 text-yellow-400" />
                </div>
                <h2 className="text-lg font-bold text-black">Paylaşımlı Yolculuk</h2>
              </div>
              <button 
                onClick={() => setShowTravelCompanionModal(false)}
                className="p-2 bg-black/10 hover:bg-black/20 text-black rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-black/80 mb-4 text-xs leading-relaxed">
              Konumunuzu yakınızdaki kişilerle paylaşarak ortak yolculuk planlayabilirsiniz. 
              Konumunuz belirttiğiniz süre boyunca paylaşılacaktır.
            </p>
            
            <div className="space-y-4">
              {/* Konum Paylaşım Süresi */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-black font-medium text-sm">Konum Paylaşım Süresi</label>
                  <span className="text-black font-medium bg-black/10 px-2 py-0.5 rounded-full text-xs">
                    {shareLocationDuration} dakika
                  </span>
                </div>
                <div className="relative pt-1">
                  <div className="overflow-hidden h-2 text-xs flex rounded-full bg-black/10">
                    <div 
                      style={{ width: `${(shareLocationDuration / 60) * 100}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-[#0e0d0d] to-[#272a2a]"
                    ></div>
                  </div>
                  <input 
                    type="range" 
                    min="5" 
                    max="60" 
                    step="5" 
                    value={shareLocationDuration} 
                    onChange={(e) => setShareLocationDuration(parseInt(e.target.value))}
                    className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
                  />
                </div>
              </div>
              
              {/* İzin Ayarları */}
              <div className="bg-black/5 rounded-xl p-3 space-y-3 border border-black/10">
                <h3 className="text-black font-medium text-xs">Gizlilik Ayarları</h3>
                
                {/* Arama İzni */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-[#0e0d0d]/10 rounded-lg">
                      <Phone className="w-3 h-3 text-[#0e0d0d]" />
                    </div>
                    <label className="text-black/80 text-xs">Arama İzni</label>
                  </div>
                  <button 
                    onClick={() => setAllowCalls(!allowCalls)}
                    className={`w-10 h-5 rounded-full transition-colors ${allowCalls ? 'bg-[#0e0d0d]' : 'bg-black/20'} relative`}
                  >
                    <span 
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${allowCalls ? 'right-0.5' : 'left-0.5'}`}
                    ></span>
                  </button>
                </div>
                
                {/* Mesaj İzni */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-[#0e0d0d]/10 rounded-lg">
                      <MessageSquare className="w-3 h-3 text-[#0e0d0d]" />
                    </div>
                    <label className="text-black/80 text-xs">Mesaj İzni</label>
                  </div>
                  <button 
                    onClick={() => setAllowMessages(!allowMessages)}
                    className={`w-10 h-5 rounded-full transition-colors ${allowMessages ? 'bg-[#0e0d0d]' : 'bg-black/20'} relative`}
                  >
                    <span 
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${allowMessages ? 'right-0.5' : 'left-0.5'}`}
                    ></span>
                  </button>
                </div>
                
                {/* İsim Gizleme */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-[#0e0d0d]/10 rounded-lg">
                      <User className="w-3 h-3 text-[#0e0d0d]" />
                    </div>
                    <label className="text-black/80 text-xs">İsmimi Gizle</label>
                  </div>
                  <button 
                    onClick={async () => {
                      // Önce local state güncelle
                      setHideNameInitials(!hideNameInitials);
                      
                      // Kullanıcı giriş yapmışsa veritabanını güncelle
                      if (userProfile && userProfile.id !== 'guest') {
                        try {
                          const { error } = await supabase
                            .from('user_profiles')
                            .update({
                              hide_name_initials: !hideNameInitials
                            })
                            .eq('id', userProfile.id);
                            
                          if (error) {
                            console.error('İsim gizleme tercihi güncellenirken hata:', error);
                            alert('İsim gizleme tercihi güncellenemedi: ' + error.message);
                            // Hata durumunda state'i geri al
                            setHideNameInitials(hideNameInitials);
                          } else {
                            console.log('İsim gizleme tercihi güncellendi:', !hideNameInitials);
                          }
                        } catch (err) {
                          console.error('İsim gizleme tercihi güncellenirken hata:', err);
                          // Hata durumunda state'i geri al
                          setHideNameInitials(hideNameInitials);
                        }
                      }
                    }}
                    className={`w-10 h-5 rounded-full transition-colors ${hideNameInitials ? 'bg-[#0e0d0d]' : 'bg-black/20'} relative`}
                  >
                    <span 
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${hideNameInitials ? 'right-0.5' : 'left-0.5'}`}
                    ></span>
                  </button>
                </div>
              </div>
              
              {/* Yolculuk Güzergahı Ekleme */}
              <div className="bg-black/5 rounded-xl p-3 space-y-3 border border-black/10">
                <h3 className="text-black font-medium text-xs">Yolculuk Güzergahı</h3>
                
                {/* Kalkış Noktası */}
                <div className="space-y-1">
                  <label className="text-black/80 text-xs flex items-center gap-2">
                    <div className="p-1 bg-[#0e0d0d]/10 rounded-lg">
                      <MapPin className="w-3 h-3 text-[#0e0d0d]" />
                    </div>
                    Kalkış Noktası
                  </label>
                  <input
                    type="text"
                    value={startLocation}
                    onChange={(e) => setStartLocation(e.target.value)}
                    placeholder="Örn: Kadıköy, İstanbul"
                    className="w-full px-3 py-1.5 bg-black/10 border border-black/10 rounded-lg text-black placeholder-black/40 text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                  />
                </div>
                
                {/* Varış Noktası */}
                <div className="space-y-1">
                  <label className="text-black/80 text-xs flex items-center gap-2">
                    <div className="p-1 bg-[#0e0d0d]/10 rounded-lg">
                      <Navigation className="w-3 h-3 text-[#0e0d0d]" />
                    </div>
                    Varış Noktası
                  </label>
                  <input
                    type="text"
                    value={endLocation}
                    onChange={(e) => setEndLocation(e.target.value)}
                    placeholder="Örn: Beşiktaş, İstanbul"
                    className="w-full px-3 py-1.5 bg-black/10 border border-black/10 rounded-lg text-black placeholder-black/40 text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-4 pt-2 border-t border-black/10">
              <button 
                onClick={() => setShowTravelCompanionModal(false)}
                className="px-3 py-2 bg-black/10 hover:bg-black/20 text-black/80 rounded-lg transition-colors text-xs font-medium"
              >
                İptal
              </button>
              <button 
                onClick={startLocationSharing}
                className="px-3 py-2 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white rounded-lg transition-colors text-xs font-medium shadow-lg shadow-black/20 flex items-center gap-1"
              >
                Konumu Paylaş
                <Timer className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Favori Duraklar */}
      <section className="px-4 sm:px-0">
        <div className="max-w-3xl mx-auto">
          <FavoriteTaxiStands />
        </div>
      </section>
    </div>
  );
}